import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { INITIAL_PAGE_SIZE } from '@/constants'
import useSearch from '@/hooks/search/useSearch'
import { feedService } from '@/services/feed/FeedService'
import { useFeedStore } from '@/stores/feed/feedStore'
import { usePriceSegmentsStore } from '@/stores/feed/priceSegmentsStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

export const useFeedLogic = () => {
  const [openFilters, setOpenFilters] = useState<boolean>(false)
  const [dataFetchingError] = useState<string>('')
  const [hasScrolled, setHasScrolled] = useState(false)

  const lastRequestedPage = useRef<number | null>(null)

  const tokenRoles = useUserInfoStore((s) => s.tokenRoles)
  const selectedOrganisation = useUserInfoStore((s) => s.selectedOrganisation)

  const {
    products,
    productsLoading,
    pagination,
    productInfoToCartPresence,
    paginatedTableLoading,
    totalElements,
    expandedRows,
    setColumnsKeys,
    setExpandedRows,
    setPagination,
    setProductsLoading,
    setPaginatedTableLoading,
  } = useFeedStore((s) => s)

  const search = useSearch()

  const {
    searchTerm,
    currentlySearchableBrands,
    currentlySearchableCategories,
    currentlySearchableStores,
    currentlySearchableCharacteristics,
    withLeftovers,
  } = useSearchStore((s) => s)

  useEffect(() => {
    const newColumnsKeys = feedService.initializeFeed(tokenRoles)
    if (newColumnsKeys) {
      setColumnsKeys(newColumnsKeys)
    }

    return () => {
      setExpandedRows([])
      setColumnsKeys([])
      usePriceSegmentsStore.getState().reset()
    }
  }, [tokenRoles, selectedOrganisation])

  useEffect(() => {
    if (!products.length) {
      if (expandedRows.length > 0) setExpandedRows([])
      return
    }

    const mandatoryExpandedRows = feedService.calculateExpandedRows(
      products,
      productInfoToCartPresence
    )

    const currentRowsJson = JSON.stringify(useFeedStore.getState().expandedRows)
    const newRowsJson = JSON.stringify(mandatoryExpandedRows)

    if (currentRowsJson !== newRowsJson) {
      setExpandedRows(mandatoryExpandedRows)
    }
  }, [products, searchTerm, productInfoToCartPresence])

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px 100px 0px',
  })

  const hasMore = products.length < totalElements

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 50) setHasScrolled(true)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Сброс при смене поиска
  useEffect(() => {
    setHasScrolled(false)
    lastRequestedPage.current = null
  }, [searchTerm, currentlySearchableBrands, currentlySearchableCategories])

  useEffect(() => {
    if (!inView || !hasScrolled) return

    const nextPage = pagination.current ?? 1

    const canLoadMore =
      !productsLoading &&
      !paginatedTableLoading &&
      hasMore &&
      products.length >= INITIAL_PAGE_SIZE

    if (canLoadMore && lastRequestedPage.current !== nextPage) {
      lastRequestedPage.current = nextPage

      search({
        query: searchTerm,
        categories: currentlySearchableCategories,
        brands: currentlySearchableBrands,
        characteristicFilters: currentlySearchableCharacteristics,
        withLeftovers,
        stores: currentlySearchableStores,
        page: nextPage,
        isNextPage: true,
        triggerNavigation: false,
      })
    }
  }, [inView, hasScrolled])

  const handlePageChange = async (page: number, pageSize: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    await feedService.handlePagination(
      page,
      pageSize,
      {
        query: searchTerm,
        categories: currentlySearchableCategories,
        brands: currentlySearchableBrands,
        characteristicFilters: currentlySearchableCharacteristics,
        withLeftovers,
      },
      {
        setPagination: (pag) =>
          setPagination({ ...pag, total: pagination.total }),
        setProductsLoading,
        setPaginatedTableLoading,
        search,
      }
    )
  }

  return {
    openFilters,
    setOpenFilters,
    dataFetchingError,
    handlePageChange,
    loadMoreRef: ref,
    hasMore,
  }
}
